import { useForecastContext } from "../../contexts/ForecastContextProvider";
import dayjs from "dayjs";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getPointForecast } from "../../services/api";
import Highcharts from "../../lib/charts/highcharts";
import HighchartsReact from "highcharts-react-official";
import { processPointDataV2 } from "./utils";
import { TbSunrise, TbSunset } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { MdSunny, MdWbCloudy } from "react-icons/md";
import { IoRainy } from "react-icons/io5";
import { FaLocationArrow } from "react-icons/fa";
import WindgramChart from "./WindgramChart";

const PointTableV2 = forwardRef(({}, ref) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { position, date, model } = useForecastContext();
    const [data, setData] = useState(null);
    const systemPrefersDark = localStorage.getItem("color-theme") === 'dark';

    // Watch the nearest horizontally-scrollable ancestor. When scrolled, drop the
    // chart's z-index so the sticky row-label column paints over the SVG's
    // overflowing altitude labels. When not scrolled, raise it so the labels
    // stay visible in the gutter to the left of the chart.
    const internalRef = useRef(null);
    const setRefs = useCallback((node) => {
        internalRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    }, [ref]);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isVScrolled, setIsVScrolled] = useState(false);

    // First-column width adapts to viewport — full sticky labels are nice on
    // desktop but eat too much horizontal real estate on a phone.
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const firstColW = isMobile ? 110 : 150;
    const colW = 56;

    useEffect(() => {
        const table = internalRef.current;
        if (!table) return undefined;
        // The horizontal- and vertical-scroll ancestors may differ; find each.
        const finder = (axis) => {
            let el = table.parentElement;
            while (el) {
                const o = window.getComputedStyle(el)[axis];
                if (o === 'auto' || o === 'scroll') return el;
                el = el.parentElement;
            }
            return null;
        };
        const hx = finder('overflowX');
        const vy = finder('overflowY');
        const onScroll = () => {
            if (hx) setIsScrolled(hx.scrollLeft > 0);
            if (vy) setIsVScrolled(vy.scrollTop > 0);
        };
        onScroll();
        const els = [...new Set([hx, vy].filter(Boolean))];
        els.forEach((el) => el.addEventListener('scroll', onScroll, { passive: true }));
        return () => els.forEach((el) => el.removeEventListener('scroll', onScroll));
    }, [data]);

    useEffect(() => {
        if (position && date && model) {
            (async () => {
                const times = Array.from({ length: 3 }, (_, i) => {
                    return Array.from({ length: 12 }, (_, j) => {
                        const hour = j + 8;
                        return dayjs(date).add(i, 'day').hour(hour).minute(model === 'ICON' ? 0 : 30).utc().format('YYYYMMDDHHmm');
                    });
                }).flat();
                setData(null);
                const raw = await getPointForecast(position.lat, position.lng, times, model, true);
                setData(processPointDataV2(raw, date, model, times, currentLanguage));
            })();
        }
    }, [date, model, position, currentLanguage]);

    const surfaceTemperatureChartOptions = useMemo(() => ({
        title: { text: '' },
        plotOptions: {
            series: {
                pointPlacement: 'on',
                animation: { duration: 1500, colorByPoint: true },
                threshold: null,
                marker: { enabled: false },
                enableMouseTracking: false,
            },
        },
        chart: { type: 'line', height: 150, marginBottom: 10 },
        legend: { enabled: false },
        xAxis: { labels: { enabled: false }, tickLength: 0 },
        yAxis: {
            max: data?.surfaceTemperature ? Math.max(...data?.surfaceTemperature.map(item => item[1])) + 5 : 30,
            gridLineWidth: 1,
            gridLineColor: 'rgba(128, 128, 128, 0.1)',
            title: '',
            labels: {
                format: `{value} °C`,
                style: { fontSize: '11px', color: systemPrefersDark ? '#FFF' : '#000' },
                x: -20,
            },
            endOnTick: false,
            tickInterval: 5,
        },
        series: [
            { type: 'line', color: 'red', data: data?.surfaceTemperature, lineWidth: 3 },
            { type: 'line', color: '#1D45AF', data: data?.surfaceDewpoint, lineWidth: 3 },
        ],
        tooltip: { enabled: false },
    }), [data, systemPrefersDark]);

    const pressureChartOptions = useMemo(() => ({
        title: { text: '' },
        plotOptions: {
            series: {
                pointPlacement: 'on',
                animation: { duration: 1500, colorByPoint: true },
                threshold: null,
                marker: { enabled: false },
                enableMouseTracking: false,
            },
        },
        chart: { type: 'line', height: 115, marginBottom: 10 },
        legend: { enabled: false },
        xAxis: { labels: { enabled: false }, tickLength: 0 },
        yAxis: {
            gridLineWidth: 1,
            gridLineColor: 'rgba(128, 128, 128, 0.1)',
            title: '',
            labels: {
                format: `{value} hPa`,
                style: { fontSize: '11px', color: systemPrefersDark ? '#FFF' : '#000' },
                x: -15,
            },
            tickInterval: 5,
        },
        series: [{ type: 'line', color: 'pink', data: data?.mslPressure, lineWidth: 3 }],
        tooltip: { enabled: false },
    }), [data, systemPrefersDark]);

    const renderCloudCover = useCallback((type) => {
        const cloudCover = [];
        for (let i = 0; i < data?.times?.length; i++) {
            cloudCover.push(<td key={`${type}${i}`}
                style={{ backgroundColor: data?.rawData?.[type][i] > 0 ? `rgba(150, 150, 150,${data?.rawData[type][i] / 100}` : systemPrefersDark ? '#004080' : 'lightblue' }}></td>);
        }
        return cloudCover;
    }, [data]);

    const renderCuClouds = useCallback(() => {
        const cuClouds = [];
        for (let i = 0; i < data?.times?.length; i++) {
            cuClouds.push(<td key={`CFRACL${i}`}
                style={{ backgroundColor: data?.rawData?.['CFRACL'][i] > .2 ? `rgba(150, 150, 150,${data?.rawData['CFRACL'][i] / 100}` : systemPrefersDark ? '#004080' : 'lightblue' }}>
                {data?.rawData?.['accrain'][i] <= 0 ? data?.rawData?.['zsfclcldif'][i] > 0 ?
                    <MdWbCloudy size={20} className="w-full text-[#ECF0F1]" /> :
                    data?.rawData?.['CFRACM'][i] < .1 && data?.rawData?.['CFRACH'][i] < .1 && data?.rawData?.['CFRACL'][i] < .15 &&
                    <MdSunny size={20} className="w-full text-[#FFD700]" /> :
                    data?.rawData?.['accrain'][i] > 0 && <IoRainy size={20} className="w-full text-[#919FA1]" />}
            </td>);
        }
        return cuClouds;
    }, [data]);

    const renderSurfaceHeating = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`sfcshf${i}`} style={{ backgroundColor: `rgba(255, 255, 0,${data?.rawData['sfcshf'][i] / 400}` }}></td>);
        }
        return out;
    }, [data]);

    const renderThermalStrength = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`wstar${i}`} style={{ backgroundColor: `rgba(255, 0, 0,${data?.rawData['wstar'][i] / 12}` }}>
                {Math.round(10 * data?.rawData['wstar'][i]) / 10}
            </td>);
        }
        return out;
    }, [data]);

    const renderSoarableMsl = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`thwcrit${i}`}>{Math.round(data?.rawData['hwcrit'][i])}</td>);
        }
        return out;
    }, [data]);

    const renderSoarableAgl = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`tdwcrit${i}`}>{10 * Math.round(data?.rawData['dwcrit'][i] / 10)}</td>);
        }
        return out;
    }, [data]);

    const renderSurfaceTemperature = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`sfctemp${i}`}
                style={{ backgroundColor: data?.rawData?.['sfctemp'][i] < 15 ? `rgba(0, 0, 255,${(15 - data?.rawData['sfctemp'][i]) / 100}` : `rgba(255, 0, 0,${(data?.rawData['sfctemp'][i] - 15) / 100}` }}>
                {Math.round(data?.rawData['sfctemp'][i])}°
            </td>);
        }
        return out;
    }, [data]);

    const renderSurfaceDewpoint = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`sfcdewpt${i}`}>{Math.round(data?.rawData['sfcdewpt'][i])}°</td>);
        }
        return out;
    }, [data]);

    const renderWind = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`wind${i}`}
                style={{ backgroundColor: `rgba(0, 0, 255,${(data?.rawData['sfcwindspd'][i] - 2) / 35}` }}>
                {Math.round(data?.rawData['sfcwindspd'][i])}m/s
                <br />
                <div className="flex flex-row items-center gap-2">
                    {Math.round(2 * data?.rawData['sfcwinddir'][i])}°
                    <span style={{ transform: `rotate(${parseInt(2 * data?.rawData['sfcwinddir'][i]) + 135 || 135}deg)` }}>
                        <FaLocationArrow className='mr-1' size={12} />
                    </span>
                </div>
                <small>{Math.round(data?.rawData['sfcgust'][i])}m/s</small>
            </td>);
        }
        return out;
    }, [data]);

    const renderThermalTopWind = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            out.push(<td key={`bltopwindspd${i}`}
                style={{ backgroundColor: `rgba(0, 0, 255,${(data?.rawData['bltopwindspd'][i] - 4) / 35}` }}>
                {Math.round(data?.rawData['bltopwindspd'][i])}m/s
                <br />
                <div className="flex flex-row items-center gap-2">
                    {Math.round(2 * data?.rawData['bltopwinddir'][i])}°
                    <span style={{ transform: `rotate(${parseInt(2 * data?.rawData['bltopwinddir'][i]) + 135 || 135}deg)` }}>
                        <FaLocationArrow className='mr-1' size={12} />
                    </span>
                </div>
            </td>);
        }
        return out;
    }, [data]);

    const renderDensityAltitude = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            const v = data?.da?.[i];
            out.push(<td key={`da${i}`}>{v == null ? '—' : v}</td>);
        }
        return out;
    }, [data]);

    const renderPotentialFlightDistance = useCallback(() => {
        const out = [];
        for (let i = 0; i < data?.times?.length; i++) {
            const v = data?.potfd?.[i];
            const alpha = v == null ? 0 : Math.min(1, v / 500);
            out.push(<td key={`potfd${i}`}
                style={{ backgroundColor: v == null ? undefined : `rgba(34, 197, 94, ${alpha})` }}>
                {v == null ? '—' : v}
            </td>);
        }
        return out;
    }, [data]);

    // CAPE (J/kg) — convective potential / overdevelopment risk for paragliders.
    // Color thresholds chosen from typical convective forecasting:
    //   < 100  : calm (no tint)
    //   100-300: yellow (moderate)
    //   300-1000: orange (active convection — watch overdevelopment)
    //   > 1000 : red (cumulonimbus / thunderstorm risk)
    const renderCape = useCallback(() => {
        const out = [];
        const timesteps = data?.windgram?.timesteps || [];
        for (let i = 0; i < data?.times?.length; i++) {
            const v = parseFloat(timesteps[i]?.cape);
            let bg;
            if (!isFinite(v) || v < 100) bg = undefined;
            else if (v < 300) bg = `rgba(250, 204, 21, ${Math.min(1, (v - 100) / 200 * 0.6)})`;
            else if (v < 1000) bg = `rgba(249, 115, 22, ${Math.min(1, (v - 300) / 700 * 0.8 + 0.2)})`;
            else bg = `rgba(220, 38, 38, ${Math.min(1, (v - 1000) / 1500 * 0.4 + 0.6)})`;
            out.push(<td key={`cape${i}`} style={{ backgroundColor: bg }}>
                {isFinite(v) ? Math.round(v) : '—'}
            </td>);
        }
        return out;
    }, [data]);

    // Combined cloud-cover row. Three vertically stacked bands per cell
    // (top=High, mid=Mid, bottom=Low) with opacity = fraction/100. The Low band
    // also folds in cu_oktas (cumulus in eighths, scaled to %) because the
    // model reports cumulus cover separately from the cfracl low-cloud band.
    // Background is sky-blue so empty cells read as "clear sky", not voids.
    const renderCloudCoverCombined = useCallback(() => {
        const out = [];
        const timesteps = data?.windgram?.timesteps || [];
        const bg = systemPrefersDark ? 'rgb(28, 48, 75)' : 'rgb(214, 230, 246)';
        for (let i = 0; i < data?.times?.length; i++) {
            const ts = timesteps[i];
            const h = ts?.cfrach || 0;
            const m = ts?.cfracm || 0;
            const cuPct = Math.min(100, (parseFloat(ts?.cu_oktas) || 0) * 12.5);
            const l = Math.max(ts?.cfracl || 0, cuPct);
            const band = (frac) => ({
                flex: 1,
                background: `rgba(150, 150, 150, ${Math.min(1, frac / 100)})`,
            });
            out.push(<td key={`cf${i}`} style={{ padding: 0, background: bg }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 30 }}>
                    <div style={band(h)} title={`High ${Math.round(h)}%`} />
                    <div style={band(m)} title={`Mid ${Math.round(m)}%`} />
                    <div style={band(l)} title={`Low ${Math.round(l)}%`} />
                </div>
            </td>);
        }
        return out;
    }, [data, systemPrefersDark]);

    if (!data) {
        return (<div className="flex justify-center items-center h-full flex-1 bg-zinc-100 dark:bg-slate-800 w-full">
            <img
                src="/assets/images/logo.png"
                className="h-12 sm:h-16 w-auto animate-pulse dark:invert dark:brightness-0"
                alt="loading"
            />
        </div>);
    }

    const hasNearestObs = Array.isArray(data.ap) && data.ap.length > 0;

    return <>
        <table className={`text-xs point-table table-fixed bg-white dark:bg-slate-800 text-black dark:text-white${isVScrolled ? ' v-scrolled' : ''}`} ref={setRefs} style={{ width: `${firstColW + data.times.length * colW}px` }}>
            <colgroup>
                {/* First column: responsive width (120px on mobile, 170px at sm+)
                    matched to the sticky <th> Tailwind classes below. Remaining
                    columns: fixed 60px each. Combined with `table-layout: fixed`,
                    this guarantees uniform data-column widths. */}
                <col style={{ width: `${firstColW}px` }} />
                <col span={data.times.length} style={{ width: `${colW}px` }} />
            </colgroup>
            <thead>
                <tr className="h-[31px]">
                    <th className="border-b-2 min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 top-0 z-40 bg-white dark:bg-slate-800">{new Date().toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' }).substring(4)}</th>
                    {data.dates.map((dt, index) => <td className="border-b-2 sticky top-0 z-[25] bg-white dark:bg-slate-800" key={index} colSpan={12}>
                        <span className="float-left flex items-center gap-2">
                            <TbSunrise size={18} className="text-[#FFD700]" />
                            {dayjs.unix(data?.rawData['twilights'][index * 2]).format('HH:mm')}
                        </span>
                        {dt}
                        <span className="float-right flex items-center gap-2">
                            {dayjs.unix(data?.rawData['twilights'][index * 2 + 1]).format('HH:mm')}
                            <TbSunset size={18} className="text-[#eeaf61]" />
                        </span>
                    </td>)}
                </tr>
                <tr className="h-[31px]">
                    <th className="border-b-2 min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 top-[31px] z-40 bg-white dark:bg-slate-800">{t('Time')}</th>
                    {data.times.map((time, index) => <td className="border-b-2 sticky top-[31px] z-[25] bg-white dark:bg-slate-800" key={index}>{time}</td>)}
                </tr>
            </thead>
            <tbody>
                <tr className="h-[200px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800 text-[#ff0000]">
                        {t('Soarable Height MSL (m)')}
                        <br />
                        <span className="text-pink-400">{t('Condensation Level')}</span>
                    </th>
                    <td
                        colSpan={data.times.length}
                        style={{ position: 'relative', zIndex: isScrolled ? 0 : 20, overflow: 'visible' }}
                    >
                        <WindgramChart data={data.windgram} width={data.times.length * colW} />
                    </td>
                </tr>

                {/* Sky cover summary: three vertically stacked bands per cell
                    (High / Mid / Low). The WindgramChart's cldfra contour shows
                    altitude-resolved cloud, this row shows the sky-cover totals
                    a pilot would actually see looking up. */}
                <tr className="h-[36px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">
                        {t('Sky Cover')}
                        <br />
                        <small className="text-gray-500 dark:text-gray-400">{t('High / Mid / Low')}</small>
                    </th>
                    {renderCloudCoverCombined()}
                </tr>
                <tr className="h-[50px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Soarable Height MSL (m)')}</th>
                    {renderSoarableMsl()}
                </tr>
                <tr className="h-[50px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Soarable Height AGL (m)')}</th>
                    {renderSoarableAgl()}
                </tr>
                <tr className="h-[50px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Thermal Strength (m/s)')}</th>
                    {renderThermalStrength()}
                </tr>
                <tr className="h-[30px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Sfc Heating')}</th>
                    {renderSurfaceHeating()}
                </tr>
                <tr className="h-[110px]">
                    <th rowSpan={2} className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800 text-[#ff0000]">
                        {t('Sfc Temp (°C)')}
                    </th>
                    <td colSpan={data.times.length}>
                        <HighchartsReact highcharts={Highcharts} options={surfaceTemperatureChartOptions} />
                    </td>
                </tr>
                <tr className="h-[50px]">
                    <th className="hidden"></th>
                    {renderSurfaceTemperature()}
                </tr>
                <tr className="h-[50px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800 text-blue-700">{t('Sfc Dewpoint (°C)')}</th>
                    {renderSurfaceDewpoint()}
                </tr>
                <tr className="h-[70px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Wind (2m AGL)')} <br /><small>{t('Gusts')}</small></th>
                    {renderWind()}
                </tr>
                <tr className="h-[50px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Thermal Top Wind')}</th>
                    {renderThermalTopWind()}
                </tr>
                <tr className="h-[85px]">
                    <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">
                        {t('Pressure (hPa)')}
                    </th>
                    <td colSpan={data.times.length}>
                        <HighchartsReact highcharts={Highcharts} options={pressureChartOptions} />
                    </td>
                </tr>

                {/* CAPE / Density Altitude / PFD are not computed for ICON
                    (the API fills them with zeros) — hide the rows there. */}
                {model !== 'ICON' && (
                    <>
                        <tr className="h-[40px]">
                            <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">
                                {t('CAPE (J/kg)')}
                                <br />
                                <small className="text-gray-500 dark:text-gray-400">{t('Convective potential')}</small>
                            </th>
                            {renderCape()}
                        </tr>
                        <tr className="h-[40px]">
                            <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Density Altitude (m)')}</th>
                            {renderDensityAltitude()}
                        </tr>
                        <tr className="h-[40px]">
                            <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Potential Flight Distance (km)')}</th>
                            {renderPotentialFlightDistance()}
                        </tr>
                    </>
                )}

                {hasNearestObs && (
                    <tr className="h-[40px]">
                        <th className="min-w-[110px] max-w-[110px] sm:min-w-[150px] sm:max-w-[150px] sticky left-0 z-10 bg-white dark:bg-slate-800">{t('Nearest Obs')}</th>
                        <td colSpan={data.times.length}>
                            {data.ap.map((station, i) => (
                                <span key={i} className="mr-3 text-[10px]">
                                    {station.name} ({station.distance ?? '?'} km)
                                </span>
                            ))}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </>;
});

export default PointTableV2;
